import './admin.css';
import { t, onLang } from './i18n';
import { tierLabel } from './tier';
import type { AdminSnapshot, AdminRoom, AdminMember, AdminMatch, PlayerProfile } from '../net/protocol';

/**
 * Admin oversight dashboard: a 🛡 FAB (visible only to admin accounts) toggling a
 * live panel of everyone connected, every open room (seats, spectators, config)
 * and the recent chat within each. The App owns the socket; this only renders
 * snapshots pushed via `setSnapshot`, and reports open/close so the App can
 * subscribe/unsubscribe to the live feed.
 */
export interface AdminPanelCallbacks {
  onSubscribe(): void;
  onUnsubscribe(): void;
  onHistory(name: string): void;
  onSetTier(targetId: string, tier: number): void;
}

// Handicap tiers (점) an admin can assign — mirrors TIERS in server/ranking.mjs.
const TIERS = [30, 50, 80, 100, 120, 150, 200, 250, 300, 400, 500];

const fmtAgo = (ts: number, now: number): string => {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

const isBusy = (s: string) => s.startsWith('playing') || s === 'spectating';

export class AdminPanel {
  private readonly fab = el('button', 'interactive');
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly head = el('div', 'admin-head');
  private readonly body = el('div', 'admin-body');
  private isAdmin = false;
  private open = false;

  constructor(root: HTMLElement, private cb: AdminPanelCallbacks) {
    this.fab.id = 'admin-fab';
    this.fab.textContent = '🛡';
    this.fab.title = t('admin.title');
    this.fab.style.display = 'none';
    onLang(() => (this.fab.title = t('admin.title')));
    this.fab.onclick = () => this.toggle();

    this.panel.id = 'admin-panel';
    this.panel.style.display = 'none';
    this.panel.append(this.head, this.profileBox, this.body);
    this.profileBox.style.display = 'none';
    root.append(this.fab, this.panel);
  }

  private readonly profileBox = el('div', 'admin-profile');
  private now = 0;

  /** Show/hide the FAB based on whether this account is an admin. */
  setAdmin(isAdmin: boolean): void {
    this.isAdmin = isAdmin;
    this.fab.style.display = isAdmin ? '' : 'none';
    if (!isAdmin && this.open) this.toggle();
  }

  private toggle(): void {
    this.open = !this.open;
    this.panel.style.display = this.open ? 'flex' : 'none';
    if (this.open) this.cb.onSubscribe();
    else this.cb.onUnsubscribe();
  }

  setSnapshot(s: AdminSnapshot): void {
    if (!this.open) return;
    this.now = s.now;
    this.head.textContent = t('admin.summary', {
      online: String(s.stats.connections),
      rooms: String(s.rooms.length),
    });
    this.body.replaceChildren();
    this.body.append(this.renderMembers(s.members), this.renderMatches(s.matches), this.renderRooms(s.rooms));
  }

  /** Render the per-player profile + history drill-down (from a member click). */
  setProfile(p: PlayerProfile | null): void {
    if (!p) {
      this.profileBox.style.display = 'none';
      return;
    }
    this.profileBox.replaceChildren();
    const head = el('div', 'admin-profhead');
    const nm = el('span', 'admin-profname');
    nm.textContent = p.name + (p.bot ? ' 🤖' : '');
    const stat = el('span', 'admin-profstat');
    const rec = p.recommend !== p.tier ? ` (rec ${p.recommend})` : '';
    stat.textContent = `${tierLabel(p.tier)}${rec} · Elo ${p.rating} · ${p.wins}-${p.losses}`;
    // admin-only ladder-tier control: promote/relegate this player's 점
    const tierSel = el('select', 'admin-proftier');
    for (const tv of TIERS) {
      const opt = el('option');
      opt.value = String(tv);
      opt.textContent = `${tv}점`;
      if (tv === p.tier) opt.selected = true;
      tierSel.append(opt);
    }
    tierSel.title = t('admin.setTier');
    tierSel.onchange = () => this.cb.onSetTier(p.id, Number(tierSel.value));
    const close = el('button', 'admin-profclose');
    close.textContent = t('mainmenu.back');
    close.onclick = () => (this.profileBox.style.display = 'none');
    head.append(nm, stat, tierSel, close);
    this.profileBox.append(head);
    const list = el('div', 'admin-profmatches');
    if (p.matches.length === 0) list.append(this.emptyLine());
    for (const m of p.matches) {
      const row = el('div', `admin-profmrow ${m.won ? 'win' : 'loss'}`);
      const res = el('span', 'admin-profres');
      res.textContent = m.won ? 'W' : 'L';
      const opp = el('span', 'admin-profopp');
      opp.textContent = `vs ${m.opponent}`;
      const d = el('span', 'admin-profdelta');
      d.textContent = `${m.delta >= 0 ? '+' : ''}${m.delta} → ${m.elo}`;
      const ago = el('span', 'admin-profago');
      ago.textContent = fmtAgo(m.ts, this.now);
      row.append(res, opp, d, ago);
      list.append(row);
    }
    this.profileBox.append(list);
    this.profileBox.style.display = 'flex';
  }

  private renderMembers(members: AdminMember[]): HTMLElement {
    const sec = el('div', 'admin-sec');
    const h = el('div', 'admin-sech');
    h.textContent = `${t('admin.members')} · ${members.length}`;
    sec.append(h);
    for (const m of members) {
      const row = el('div', 'admin-mrow');
      const dot = el('span', `admin-dot ${isBusy(m.status) ? 'busy' : 'idle'}`);
      const name = el('span', 'admin-mname');
      name.textContent = m.name;
      const tag = el('span', 'admin-tag');
      tag.textContent = m.bot ? 'BOT' : m.registered ? 'REG' : 'GUEST';
      tag.classList.add(m.bot ? 'bot' : m.registered ? 'reg' : 'guest');
      // ranking stats when the player is on the ladder; click for full history
      const stat = el('span', 'admin-mstat');
      if (m.rating != null && m.id) {
        stat.textContent = `${tierLabel(m.tier ?? 30)} · ${m.rating} · ${m.wins}-${m.losses}`;
        row.classList.add('clickable');
        const id = m.id;
        row.onclick = () => this.cb.onHistory(id);
      } else {
        stat.textContent = m.status + (m.room ? ` · ${m.room}` : '');
      }
      row.append(dot, name, tag, stat);
      sec.append(row);
    }
    if (members.length === 0) sec.append(this.emptyLine());
    return sec;
  }

  private renderMatches(list: AdminMatch[]): HTMLElement {
    const sec = el('div', 'admin-sec');
    const h = el('div', 'admin-sech');
    h.textContent = t('admin.recentMatches');
    sec.append(h);
    for (const m of list) {
      const row = el('div', 'admin-matchrow');
      const txt = el('span', 'admin-matchtxt');
      txt.textContent = `${m.winner} ▸ ${m.loser}`;
      const d = el('span', 'admin-matchd');
      d.textContent = `+${m.winnerDelta}/${m.loserDelta}`;
      const ago = el('span', 'admin-profago');
      ago.textContent = fmtAgo(m.ts, this.now);
      row.append(txt, d, ago);
      sec.append(row);
    }
    if (list.length === 0) sec.append(this.emptyLine());
    return sec;
  }

  private renderRooms(roomList: AdminRoom[]): HTMLElement {
    const sec = el('div', 'admin-sec');
    const h = el('div', 'admin-sech');
    h.textContent = `${t('admin.rooms')} · ${roomList.length}`;
    sec.append(h);
    for (const r of roomList) sec.append(this.renderRoom(r));
    if (roomList.length === 0) sec.append(this.emptyLine());
    return sec;
  }

  private renderRoom(r: AdminRoom): HTMLElement {
    const box = el('div', 'admin-room');
    const head = el('div', 'admin-roomhead');
    const code = el('span', 'admin-roomcode');
    code.textContent = r.code;
    const meta = el('span', 'admin-roommeta');
    meta.textContent = `${r.variant} · ${r.format} · ${r.status}`;
    const badge = el('span', `admin-roomstatus ${r.status}`);
    badge.textContent = r.status;
    head.append(code, meta, badge);
    box.append(head);

    // seats
    const seats = el('div', 'admin-seats');
    r.seats.forEach((s, i) => {
      const cell = el('span', 'admin-seat');
      if (!s) {
        cell.classList.add('empty');
        cell.textContent = `${i + 1}: —`;
      } else {
        const host = s.id === r.hostId ? '★' : '';
        const ready = s.ready ? '✓' : '';
        const away = s.present ? '' : ' (away)';
        cell.textContent = `${i + 1}: ${host}${s.name} ${s.level}점 ${ready}${away}`;
        if (!s.present) cell.classList.add('away');
      }
      seats.append(cell);
    });
    box.append(seats);

    // spectators
    if (r.spectators.length) {
      const spec = el('div', 'admin-spec');
      spec.textContent = `👁 ${r.spectators.map((v) => v.name).join(', ')}`;
      box.append(spec);
    }

    // chat
    if (r.chat.length) {
      const chat = el('div', 'admin-chat');
      for (const line of r.chat) {
        const cl = el('div', 'admin-chatline');
        cl.textContent = line.system ? `— ${line.text}` : `${line.name}: ${line.text}`;
        chat.append(cl);
      }
      box.append(chat);
    }
    return box;
  }

  private emptyLine(): HTMLElement {
    const e = el('div', 'admin-empty');
    e.textContent = t('admin.none');
    return e;
  }
}
